import {
  addOrderItems,
  confirmOrder,
  createOrder as createOrderApi,
  customerOrderHistory,
  deleteOrder as deleteOrderApi,
  getOrder,
  getOrderReceipt,
  getOrders,
  getOrderWithRelation,
  searchOrders,
  updateOrder as updateOrderApi,
} from "@/api/order-api";
import {
  buildCacheKey,
  useCollectionData,
  useMutation,
  usePaginatedData,
} from "@/hooks/use-api";
import { toast } from "@/hooks/use-toast";
import {
  OrderCreateFormValues,
  OrderSortOption,
  OrderStatus,
  OrderUpdateValues
} from "@/types/order-type";

// Constants
const ORDERS_ENDPOINT = "/orders";

// Hook for getting paginated orders
export function useOrders(params?: {
  page?: number;
  size?: number;
  searchTerm?: string;
  sort?: OrderSortOption;
  status?: string;
  customerId?: string;
  orderDate?: string;
}) {
  const cacheKey = buildCacheKey(ORDERS_ENDPOINT, params);

  return usePaginatedData(cacheKey, () => getOrders(params), {
    refreshInterval: 30000,
    revalidateOnFocus: true,
  });
}

// Hook for getting a single order
export function useOrder(id: string | null) {
  const cacheKey = id ? `${ORDERS_ENDPOINT}/${id}` : null;

  return useCollectionData(
    cacheKey,
    id ? () => getOrder(id) : () => Promise.resolve({ data: null } as any),
    {
      refreshInterval: 0,
      revalidateOnFocus: true,
    }
  );
}

export function useOrderWithRelation(id: string) {
  return useCollectionData(
    `${ORDERS_ENDPOINT}/${id}/relation`,
    () => getOrderWithRelation(id),
    {
      refreshInterval: 0,
      revalidateOnFocus: true,
    }
  );
}

// Hook for creating an order
export function useCreateOrder() {
  return useMutation(
    async (data: OrderCreateFormValues) => {
      const response = await createOrderApi(data);
      return response;
    },
    {
      onSuccess: (response) => {
        toast({
          title: response.statusText || "Order created",
          description: response.message,
        });
      },
      onError: (error) => {
        toast({
          title: error?.statusText || "Order creation failed",
          description:
            error?.message ||
            "We could not create the order. Please try again later.",
          variant: "destructive",
        });
      },
      invalidateKeys: [ORDERS_ENDPOINT],
    }
  );
}

// Hook for updating an order
export function useUpdateOrder() {
  return useMutation(
    ({ id, data }: { id: string; data: OrderUpdateValues }) =>
      updateOrderApi(id, data),
    {
      onSuccess: (response, variables) => {
        toast({
          title: response.statusText,
          description: response.message,
        });
      },
      onError: (error) => {
        toast({
          title: error.statusText,
          description: error.message,
          variant: "destructive",
        });
      },
      invalidateKeys: [ORDERS_ENDPOINT],
    }
  );
}

// Hook for adding items to an existing order
export function useAddOrderItems() {
  return useMutation(
    ({
      id,
      items,
    }: {
      id: string;
      items: { variantId: string; quantity: number }[];
    }) => addOrderItems(id, items),
    {
      onSuccess: (response, variables) => {
        toast({
          title: response.statusText,
          description: response.message,
        });
      },
      onError: (error) => {
        toast({
          title: error.statusText,
          description: error.message,
          variant: "destructive",
        });
      },
      invalidateKeys: [
        "/orders",
        (variables: any) => `/orders/${variables?.id}`,
        (variables: any) => `/orders/${variables?.id}/relation`,
      ].filter(Boolean) as any,
    }
  );
}

// Hook for deleting an order
export function useDeleteOrder() {
  return useMutation(deleteOrderApi, {
    onSuccess: (response) => {
      toast({
        title: response.statusText,
        description: response.message,
      });
    },
    onError: (error) => {
      toast({
        title: error.statusText,
        description: error.message,
        variant: "destructive",
      });
    },
    invalidateKeys: [ORDERS_ENDPOINT],
  });
}

// Hook for searching orders
export function useSearchOrders(
  searchTerm: string,
  options?: {
    page?: number;
    size?: number;
    sort?: OrderSortOption;
  }
) {
  const params = {
    searchTerm,
    page: options?.page || 0,
    size: options?.size || 20,
    sort: options?.sort || ("orderDate,desc" as OrderSortOption),
  };

  const cacheKey = buildCacheKey(`${ORDERS_ENDPOINT}/search`, params);

  return usePaginatedData(
    searchTerm ? cacheKey : null, // Only fetch if searchTerm exists
    searchTerm
      ? () => searchOrders(searchTerm, params.page, params.size, params.sort)
      : () => Promise.resolve({ data: { content: [] } } as any),
    {
      refreshInterval: 0, // Don't auto-refresh search results
      revalidateOnFocus: false,
    }
  );
}

// Utility functions for formatting
export const formatCurrency = (amount: string | number): string => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const getOrderStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    [OrderStatus.DRAFT]: "bg-black",
    [OrderStatus.CONFIRMED]: "bg-blue-500",
    [OrderStatus.RETURNED]: "bg-purple-500",
    [OrderStatus.CANCELLED]: "bg-red-500",
    [OrderStatus.REFUNDED]: "bg-orange-500",
    [OrderStatus.PAID]: "bg-emerald-600",
  };
  return colors[status] || "bg-gray-500";
};

export const getPaymentStatusColor = (status: string): string => {
  return status === "Paid"
    ? "text-emerald-600 border-emerald-200"
    : "text-amber-600 border-amber-200";
};

// Hook for confirming an order
export function useConfirmOrder() {
  return useMutation((orderId: string) => confirmOrder(orderId), {
    onSuccess: (response, orderId) => {
      toast({
        title: response.statusText,
        description: response.message,
      });
    },
    onError: (error) => {
      toast({
        title: error.statusText,
        description: error.message,
        variant: "destructive",
      });
    },
    invalidateKeys: [
      ORDERS_ENDPOINT,
      (orderId: string) => `${ORDERS_ENDPOINT}/${orderId}`,
      (orderId: string) => `${ORDERS_ENDPOINT}/${orderId}/relation`,
    ] as any,
  });
}

export function useCustomerOrders(customerId: string, limit?: {}) {
  return useMutation((customerId: string) => customerOrderHistory(customerId), {
    onSuccess: (response) => {
      console.log("fetching customer order successfully:", response.data);
    },
    onError: (error) => {
      console.error("Error fetching customer:", error);
    },
    invalidateKeys: [ORDERS_ENDPOINT],
  });
}

export function useOrderReceipt(id: string) {
  const cacheKey = `${ORDERS_ENDPOINT}/${id}/receipt`;

  return useCollectionData(
    cacheKey,
    () => getOrderReceipt(id),
    {
      revalidateOnFocus: true,
    }
  );
}
